import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { ArrowLeft, Loader2, Save } from 'lucide-react'
import { apiGet, apiPost, slugify } from '../lib/api'
import { ProductForm, type ProductFormState } from './ProductoEditar'

interface CategoriasData {
  data: Array<{ id: string; nombre: string }>
}

interface MarcasData {
  data: Array<{ id: string; nombre: string }>
}

const emptyForm: ProductFormState = {
  sku: '',
  slug: '',
  name: '',
  description: '',
  category_id: '',
  brand_id: '',
  price: '0',
  price_2: '',
  price_3: '',
  price_4: '',
  sale_price: '',
  cost: '0',
  iva: '16',
  stock: '0',
  weight_kg: '0',
  image: '',
  is_featured: false,
  is_on_sale: false,
  is_week_sale: false,
  is_active: true,
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  tags: '',
  image2: '',
  image3: '',
  image4: '',
  image5: '',
  image_low: '',
  dim_length: '0',
  dim_width: '0',
  dim_height: '0',
  dim_text: '',
  year_from: '',
  year_to: '',
  compat_brands: '',
  compat_models: '',
  is_new: false,
  is_hot: false,
  inhibit_purchase: false,
  line: '',
  part_origin: '',
  part_brand: '',
  part_brand_id: '',
  supplier_id: '',
  regular_price: '0',
  sku_kit: '',
  previous_sku: '',
  previous_name: '',
}

export function ProductoNuevo() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [form, setForm] = useState<ProductFormState>(emptyForm)
  const [slugTouched, setSlugTouched] = useState(false)

  const { data: categorias } = useQuery<CategoriasData>({
    queryKey: ['admin-categorias-min'],
    queryFn: () => apiGet('/api/admin/categorias?tree=1'),
    staleTime: 5 * 60 * 1000,
  })

  const { data: marcas } = useQuery<MarcasData>({
    queryKey: ['admin-marcas-min'],
    queryFn: () => apiGet('/api/admin/marcas?page=1'),
    staleTime: 5 * 60 * 1000,
  })

  const createMut = useMutation<{ data: { id: string } }, Error, any>({
    mutationFn: (payload) => apiPost('/api/admin/productos', payload),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['productos'] })
      navigate({ to: '/productos/$id', params: { id: res.data.id } })
    },
  })

  function setField<K extends keyof ProductFormState>(key: K, value: ProductFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const payload = {
      sku: form.sku.trim(),
      slug: form.slug.trim() || slugify(form.name),
      name: form.name.trim(),
      description: form.description.trim() || null,
      category_id: form.category_id || null,
      brand_id: form.brand_id || null,
      price: Number(form.price) || 0,
      price_2: form.price_2 !== '' ? Number(form.price_2) : null,
      price_3: form.price_3 !== '' ? Number(form.price_3) : null,
      price_4: form.price_4 !== '' ? Number(form.price_4) : null,
      sale_price: form.sale_price !== '' ? Number(form.sale_price) : null,
      cost: Number(form.cost) || 0,
      tax_rate: form.iva !== '' ? Number(form.iva) / 100 : 0.16,
      stock: parseInt(form.stock) || 0,
      weight_kg: Number(form.weight_kg) || 0,
      image: form.image.trim(),
      is_featured: form.is_featured,
      is_on_sale: form.is_on_sale,
      is_week_sale: form.is_week_sale,
      is_active: form.is_active,
      meta_title: form.meta_title.trim() || null,
      meta_description: form.meta_description.trim() || null,
      meta_keywords: form.meta_keywords.trim() || null,
      tags: form.tags.trim() || null,
      image2: form.image2.trim() || null,
      image3: form.image3.trim() || null,
      image4: form.image4.trim() || null,
      image5: form.image5.trim() || null,
      image_low: form.image_low.trim() || null,
      dimensions_length: Number(form.dim_length) || 0,
      dimensions_width: Number(form.dim_width) || 0,
      dimensions_height: Number(form.dim_height) || 0,
      dimensions_text: form.dim_text.trim() || null,
      year_from: form.year_from.trim() || null,
      year_to: form.year_to.trim() || null,
      compatible_brands_text: form.compat_brands.trim() || null,
      compatible_models_text: form.compat_models.trim() || null,
      is_new: form.is_new,
      is_hot: form.is_hot,
      inhibit_purchase: form.inhibit_purchase,
      line: form.line.trim() || null,
      part_origin: form.part_origin.trim() || null,
      part_brand: form.part_brand.trim() || null,
      part_brand_id: form.part_brand_id || null,
      supplier_id: form.supplier_id || null,
      regular_price: Number(form.regular_price) || 0,
      sku_kit: form.sku_kit.trim() || null,
      previous_sku: form.previous_sku.trim() || null,
      previous_name: form.previous_name.trim() || null,
    }
    createMut.mutate(payload)
  }

  return (
    <>
      <Topbar
        title="Nuevo producto"
        subtitle="Crear un producto en el catálogo"
        actions={
          <>
            <button
              type="button"
              onClick={() => navigate({ to: '/productos' })}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <ArrowLeft className="size-3.5" /> Cancelar
            </button>
            <button
              type="submit"
              form="producto-form-new"
              disabled={createMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {createMut.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Save className="size-3.5" />
              )}
              Crear producto
            </button>
          </>
        }
      />

      <form id="producto-form-new" onSubmit={onSubmit} className="space-y-4 p-6">
        {createMut.isError && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {(createMut.error as Error).message}
          </div>
        )}

        <ProductForm
          form={form}
          setField={setField}
          slugTouched={slugTouched}
          setSlugTouched={setSlugTouched}
          categorias={categorias?.data ?? []}
          marcas={marcas?.data ?? []}
        />
      </form>
    </>
  )
}

export default ProductoNuevo
